import type { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@ui/Form";
import { Label } from "@ui/Label";
import NumberInput from "@ui/NumberInput";
import { RadioGroup, RadioGroupItem } from "@ui/RadioGroup";
import { useMultiStepForm } from "@/hooks";
import { PROFILE_TYPES, CONSUMPTION_TYPES } from "@/utils/step1Schema";

export const ProfileSubForm: FC = () => {
  const { form } = useMultiStepForm();
  const { t } = useTranslation();

  const isConsumptionDisabled = form.watch("step1.consumptionType") === undefined;

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="step1.predefinedProfile"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("step1.profile_subForm.q1")}</FormLabel>
            <FormControl>
              <RadioGroup
                className="gap-3 px-2 md:px-4"
                value={field.value ?? ""}
                onValueChange={field.onChange}
              >
                {PROFILE_TYPES.map(option => (
                  <div className="flex items-center space-x-3" key={option}>
                    <RadioGroupItem
                      value={option}
                      id={option}
                      data-test={`step1-profile-input-${option}`}
                    />
                    <Label data-test={`step1-profile-${option}`} htmlFor={option}>
                      {t(`step1.profile_subForm.q1_${option}`)}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage className="px-2 md:px-4" />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="step1.consumptionType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("step1.profile_subForm.q2")}</FormLabel>
            <FormControl>
              <div className="flex w-full flex-col justify-between gap-4 px-2 md:flex-row md:pr-0 md:pl-4">
                <div className="flex h-full flex-col gap-2">
                  <RadioGroup
                    value={field.value ?? ""}
                    onValueChange={field.onChange}
                    className="h-[66px] gap-3"
                  >
                    {CONSUMPTION_TYPES.map(option => (
                      <div className="flex items-center space-x-3" key={option}>
                        <RadioGroupItem
                          value={option}
                          id={option}
                          data-test={`step1-consumption-input-${option}`}
                        />
                        <Label data-test={`step1-consumption-type-${option}`} htmlFor={option}>
                          {t(`step1.profile_subForm.q2_${option}`)}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                  <FormMessage className="mt-auto" />
                </div>
                <FormField
                  control={form.control}
                  name="step1.consumption"
                  render={({ field }) => (
                    <FormItem className="gap-1">
                      <FormControl>
                        <div className="space-y-0.5">
                          <FormDescription>{t("consumption")}</FormDescription>
                          <NumberInput
                            className="h-10 w-[156px]"
                            {...field}
                            unit={t("MWh")}
                            disabled={isConsumptionDisabled}
                            dataTest="consumption-input"
                            isError={Boolean(form.formState.errors.step1?.consumption)}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
};
